import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid2 as Grid,
  Typography,
  Divider,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { personSchema } from '../../schemas/validationSchemas';
import DateField from '../ui/DateField';
import { maskPhoneBR, cepMask } from '../../utils/masks';
import { lookupCep } from '../../services/viaCepService';

/**
 * Formulário de Pessoa (Create/Update) com validação Zod.
 * Endereço estruturado alinhado com PlaceForm (CEP, Rua, Número, Complemento, Bairro, Cidade, UF).
 * Autocomplete de CEP via ViaCEP ao sair do campo.
 * Campo `address` legacy exibido somente em edição de registros antigos (readonly).
 */
export default function PersonForm({ open, onClose, onSubmit, data, loading = false }) {
  const isEdit = !!data;
  const [cepLoading, setCepLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors, isValid, isDirty },
  } = useForm({
    resolver: zodResolver(personSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      cpf: '',
      birthDate: '',
      address: '',
      addressZipCode: '',
      addressStreet: '',
      addressNumber: '',
      addressComplement: '',
      addressNeighborhood: '',
      addressCity: '',
      addressState: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (open) {
      if (data) {
        reset({
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          cpf: data.cpf || '',
          birthDate: data.birthDate ? data.birthDate.split('T')[0] : '',
          address: data.address || '',
          addressZipCode: data.addressZipCode || '',
          addressStreet: data.addressStreet || '',
          addressNumber: data.addressNumber || '',
          addressComplement: data.addressComplement || '',
          addressNeighborhood: data.addressNeighborhood || '',
          addressCity: data.addressCity || '',
          addressState: data.addressState || '',
          notes: data.notes || '',
        });
      } else {
        reset({
          name: '',
          email: '',
          phone: '',
          cpf: '',
          birthDate: '',
          address: '',
          addressZipCode: '',
          addressStreet: '',
          addressNumber: '',
          addressComplement: '',
          addressNeighborhood: '',
          addressCity: '',
          addressState: '',
          notes: '',
        });
      }
    }
  }, [data, open, reset]);

  const onSubmitForm = (formData) => {
    const processed = {
      ...formData,
      // Backend espera CEP sem hífen (StringLength(8))
      addressZipCode: formData.addressZipCode?.replace(/\D/g, '') || '',
    };
    onSubmit(processed);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCepBlur = async (cepValue) => {
    const digits = (cepValue || '').replace(/\D/g, '');
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const result = await lookupCep(digits);
      if (result) {
        if (result.street) setValue('addressStreet', result.street, { shouldValidate: true });
        if (result.neighborhood) setValue('addressNeighborhood', result.neighborhood, { shouldValidate: true });
        if (result.city) setValue('addressCity', result.city, { shouldValidate: true });
        if (result.state) setValue('addressState', result.state, { shouldValidate: true });
      }
    } finally {
      setCepLoading(false);
    }
  };

  // Exibe o campo de endereço legado apenas em edição de registros antigos
  // (address preenchido, addressStreet vazio ~ nunca passou pelo novo form)
  const showLegacyAddress = isEdit && !!data?.address && !data?.addressStreet;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? 'Editar Pessoa' : 'Nova Pessoa'}
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmitForm)}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2.5}>
            {/* Dados Pessoais */}
            <Box>
              <Typography variant="subtitle2" color="primary" gutterBottom fontWeight="bold">
                Dados Pessoais
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    autoFocus
                    label="Nome Completo"
                    fullWidth
                    {...register('name')}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    required
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Email"
                    type="email"
                    fullWidth
                    {...register('email')}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Telefone"
                        fullWidth
                        value={field.value}
                        onChange={(e) => field.onChange(maskPhoneBR(e.target.value))}
                        error={!!errors.phone}
                        helperText={errors.phone?.message || '(11) 98765-4321'}
                        required
                        disabled={loading}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="CPF"
                    fullWidth
                    {...register('cpf')}
                    error={!!errors.cpf}
                    helperText={errors.cpf?.message || 'Somente números ou XXX.XXX.XXX-XX'}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="birthDate"
                    control={control}
                    render={({ field }) => (
                      <DateField
                        label="Data de Nascimento"
                        value={field.value || ''}
                        onChange={field.onChange}
                        error={!!errors.birthDate}
                        helperText={errors.birthDate?.message}
                        disabled={loading}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Endereço */}
            <Box>
              <Typography variant="subtitle2" color="primary" gutterBottom fontWeight="bold">
                Endereço
              </Typography>

              {/* Endereço legado — somente leitura para registros antigos */}
              {showLegacyAddress && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Endereço anterior (migração):</strong> {data.address}
                  <br />
                  <Typography variant="caption">Preencha os campos abaixo para atualizar ao formato estruturado.</Typography>
                </Alert>
              )}

              <Grid container spacing={2}>
                {/* CEP — com máscara e autocomplete ViaCEP */}
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Controller
                    name="addressZipCode"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="CEP"
                        fullWidth
                        value={field.value}
                        onChange={(e) => field.onChange(cepMask(e.target.value))}
                        onBlur={(e) => {
                          field.onBlur();
                          handleCepBlur(e.target.value);
                        }}
                        error={!!errors.addressZipCode}
                        helperText={errors.addressZipCode?.message || '12345-678'}
                        disabled={loading}
                        InputProps={cepLoading ? {
                          endAdornment: (
                            <InputAdornment position="end">
                              <CircularProgress size={16} />
                            </InputAdornment>
                          ),
                        } : undefined}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 8 }}>
                  <TextField
                    label="Rua/Avenida"
                    fullWidth
                    {...register('addressStreet')}
                    error={!!errors.addressStreet}
                    helperText={errors.addressStreet?.message}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <TextField
                    label="Número"
                    fullWidth
                    {...register('addressNumber')}
                    error={!!errors.addressNumber}
                    helperText={errors.addressNumber?.message}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 5 }}>
                  <TextField
                    label="Complemento"
                    fullWidth
                    {...register('addressComplement')}
                    error={!!errors.addressComplement}
                    helperText={errors.addressComplement?.message}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="Bairro"
                    fullWidth
                    {...register('addressNeighborhood')}
                    error={!!errors.addressNeighborhood}
                    helperText={errors.addressNeighborhood?.message}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 8 }}>
                  <TextField
                    label="Cidade"
                    fullWidth
                    {...register('addressCity')}
                    error={!!errors.addressCity}
                    helperText={errors.addressCity?.message}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <TextField
                    label="UF"
                    fullWidth
                    {...register('addressState')}
                    error={!!errors.addressState}
                    helperText={errors.addressState?.message || 'SP'}
                    disabled={loading}
                    inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Observações */}
            <Box>
              <Typography variant="subtitle2" color="primary" gutterBottom fontWeight="bold">
                Observações
              </Typography>
              <TextField
                label="Notas Adicionais"
                fullWidth
                multiline
                rows={3}
                {...register('notes')}
                error={!!errors.notes}
                helperText={errors.notes?.message || 'Informações relevantes sobre a pessoa'}
                disabled={loading}
              />
            </Box>

            {/* Alert de validação */}
            {Object.keys(errors).length > 0 && (
              <Alert severity="error">
                Por favor, corrija os erros acima antes de salvar.
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={loading || !isValid || (!isDirty && !isEdit)}>
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
